import React from "react";
import { CheckRounded, CloseRounded } from "@mui/icons-material";

/**
 * A list-toolbar filter, drawn as a pill rather than a checkbox.
 *
 * A checkbox in a toolbar reads as a form field somebody has to fill in, and it
 * carries none of the page's own language — the summary tiles above these lists
 * are already "press it and it turns primary-tinted", so a filter that sits two
 * inches below them should answer to the same gesture and the same colours.
 *
 * Two shapes, one component:
 *
 *   onClick  — a toggle. `active` tints it and adds a tick, so on/off is legible
 *              at a glance across the room, not just from the state of a 13px box.
 *   onClear  — an applied filter. The chip names what is being filtered and the ×
 *              drops it, which tells a reader why the list is short. `onClear` is
 *              a nested <button>, so the chip itself is a <span> in this shape —
 *              a button inside a button is invalid and Safari swallows the inner
 *              click.
 */
const FilterChip = ({
  label,
  icon,
  active = false,
  onClick,
  onClear,
  clearTitle,
  title,
  disabled = false,
  className = "",
}) => {
  // No horizontal padding here: each shape sets its own, because px-3 and ps-3/pe-1
  // are different properties and which one wins depends on Tailwind's output order,
  // not on the order they are written in this string.
  const base =
    "inline-flex items-center gap-1.5 rounded-full border py-[5px] text-[12px] leading-[18px] whitespace-nowrap select-none transition-colors";
  const tone = active
    ? "border-primary-300 bg-primary-50 text-primary font-semibold"
    : "border-gray-200 bg-white text-slate-600 font-medium";

  const body = (
    <>
      {icon && <span className="flex items-center shrink-0">{icon}</span>}
      <span className="truncate">{label}</span>
    </>
  );

  if (onClear) {
    return (
      <span className={`${base} ${tone} ps-3 pe-1 ${className}`} title={title}>
        {body}
        <button
          type="button"
          onClick={onClear}
          title={clearTitle}
          aria-label={clearTitle || label}
          className="flex items-center rounded-full p-[2px] text-current opacity-70 hover:opacity-100 hover:bg-black/5"
        >
          <CloseRounded className="!text-[14px]" />
        </button>
      </span>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-pressed={active}
      title={title}
      className={`${base} ${tone} px-3 ${
        disabled
          ? "opacity-50 cursor-not-allowed"
          : active
            ? // Deepen the fill on hover. Reaching for the usual hover:border-primary-200
              // here would LIGHTEN the ring of a chip that is already on.
              "cursor-pointer hover:bg-primary-100"
            : "cursor-pointer hover:border-primary-200 hover:text-primary"
      } ${className}`}
    >
      {body}
      {active && <CheckRounded className="!text-[14px] shrink-0" />}
    </button>
  );
};

export default FilterChip;
